import type { Report } from "../types/models";
import { createIdempotencyKey } from "../lib/idempotency";
import { apiBlob, apiData, apiPath, type Schema } from "./api";

export async function listReports(): Promise<Report[]> {
  const data = await apiData<Schema<"ReportResponseListEnvelope">>("/reports", { query: { limit: 50, offset: 0 } });
  return data.filter(hasId);
}

export async function createReport(body: Schema<"CreateReportRequest">): Promise<Report | null> {
  const data = await apiData<Schema<"CreateReportResponseEnvelope">, Schema<"CreateReportRequest">>("/reports", {
    method: "POST",
    body,
    idempotencyKey: createIdempotencyKey()
  });
  if (!data.id) {
    return null;
  }
  return (await getReport(data.id)) ?? { id: data.id, status: data.status, kind: body.kind, format: body.format };
}

export async function getReport(id: string): Promise<Report | null> {
  const data = await apiData<Schema<"ReportResponseEnvelope">>(apiPath(`/reports/${id}`));
  return data.id ? (data as Report) : null;
}

export async function downloadReport(report: Pick<Report, "id" | "format" | "kind">): Promise<void> {
  const file = await apiBlob(apiPath(`/reports/${report.id}/download`));
  const url = URL.createObjectURL(file.blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = reportFileName(report, file.contentType, file.contentDisposition);
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function hasId<T extends { id?: string }>(item: T): item is T & { id: string } {
  return Boolean(item.id);
}

function reportFileName(report: Pick<Report, "id" | "format" | "kind">, contentType: string, contentDisposition: string): string {
  const dispositionFileName = contentDispositionFileName(contentDisposition);
  if (dispositionFileName) {
    return dispositionFileName;
  }
  const kind = safeFilePart(report.kind || "hydration");
  const id = safeFilePart(report.id).slice(0, 8);
  return `watertracker-${kind}-${id}.${reportExtension(report.format, contentType)}`;
}

function contentDispositionFileName(contentDisposition: string): string | null {
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8Match?.[1]) {
    return safeFileName(decodeURIComponent(utf8Match[1]));
  }
  const quotedMatch = /filename="([^"]+)"/i.exec(contentDisposition);
  if (quotedMatch?.[1]) {
    return safeFileName(quotedMatch[1]);
  }
  const plainMatch = /filename=([^;]+)/i.exec(contentDisposition);
  if (plainMatch?.[1]) {
    return safeFileName(plainMatch[1]);
  }
  return null;
}

function reportExtension(format: Report["format"] | undefined, contentType: string): "csv" | "json" | "pdf" | "xlsx" {
  if (format) {
    return format;
  }
  const normalized = contentType.toLowerCase();
  if (normalized.includes("spreadsheetml")) {
    return "xlsx";
  }
  if (normalized.includes("pdf")) {
    return "pdf";
  }
  if (normalized.includes("json")) {
    return "json";
  }
  return "csv";
}

function safeFileName(value: string): string {
  const cleaned = value.trim().replace(/[\\/:*?"<>|]+/g, "-");
  return cleaned || "watertracker-report";
}

function safeFilePart(value: string): string {
  return safeFileName(value).replace(/\s+/g, "-").toLowerCase();
}
